import { ApiError } from "@/services/http";
import type { JiraPeopleHealth, JiraWorkHealth } from "@/types/api";

/** `n` with the word for one or for many. */
function plural(n: number, one: string, many: string): string {
	return `${n} ${n === 1 ? one : many}`;
}

/** While Curo is still reading tickets, the section refreshes itself this often. */
export const WORK_HEALTH_POLL_MS = 10_000;

/** Poll while an import or ticket reads are still running, so counts fill in without a reload. */
export function workHealthRefetchInterval(
	health: JiraWorkHealth | undefined,
): number | false {
	if (!health) return false;
	const reading =
		health.work.pendingReads > 0 || health.projects.some((p) => p.importing);
	return reading ? WORK_HEALTH_POLL_MS : false;
}

/** The headline: how many of the people with open tickets Curo matched. */
export function matchedSummary(people: JiraPeopleHealth): string {
	if (people.assignees === 0)
		return "No one has open, assigned tickets in your approved projects yet.";
	return `${people.matched} of ${plural(people.assignees, "person", "people")} with open tickets matched to Curo.`;
}

type UnmatchedOutcome = Exclude<
	keyof JiraPeopleHealth,
	"assignees" | "matched"
>;

/** One reason someone is not matched, and what the admin can do about it. */
export interface UnmatchedRow {
	outcome: UnmatchedOutcome;
	count: number;
	label: string;
	action: string | null;
}

const UNMATCHED: Record<
	UnmatchedOutcome,
	{ label: (n: number) => string; action: string | null }
> = {
	emailHidden: {
		label: (n) => `${plural(n, "hides", "hide")} their email in Jira`,
		action:
			"Ask them to set their email to “Anyone” in their Atlassian profile (Profile and visibility → Contact).",
	},
	noSeat: {
		label: (n) => `${plural(n, "has", "have")} a Jira email with no seat here`,
		action:
			"Invite the email they use in Jira, or ask them to change it. Curo picks up the change within a day.",
	},
	invitedNotJoined: {
		label: (n) => `${n} invited but not joined yet`,
		action: "They need to accept their Curo invite.",
	},
	checking: {
		label: (n) => `${n} still being checked`,
		action:
			"Curo checks them the next time it reads their tickets, usually within a day.",
	},
	app: {
		label: (n) => `${plural(n, "is an app", "are apps")}, not people`,
		action: null,
	},
};

/** The reasons with anyone in them, most actionable first. */
export function unmatchedRows(people: JiraPeopleHealth): UnmatchedRow[] {
	return (Object.keys(UNMATCHED) as UnmatchedOutcome[])
		.filter((outcome) => people[outcome] > 0)
		.map((outcome) => ({
			outcome,
			count: people[outcome],
			label: UNMATCHED[outcome].label(people[outcome]),
			action: UNMATCHED[outcome].action,
		}));
}

/** What the admin should know about Curo's reading of Jira; nothing when all is well. */
export function workNotices(work: JiraWorkHealth["work"]): string[] {
	const notices: string[] = [];
	if (work.paused)
		notices.push(
			"Curo has paused reading Jira for your organization. Contact Curo if this continues.",
		);
	if (!work.claimsEnabled)
		notices.push(
			"Matched people's work is not in their learning plans yet: work personalization is switched off for now.",
		);
	if (work.pendingReads > 0)
		notices.push(`Reading ${plural(work.pendingReads, "ticket", "tickets")}…`);
	if (work.failedReads > 0)
		notices.push(
			`${plural(work.failedReads, "ticket", "tickets")} could not be read in the last week. Curo reads them again when they next change in Jira.`,
		);
	return notices;
}

/** A project's line: its open tickets and how many have an assignee. */
export function projectLine(
	openTickets: number,
	assignedTickets: number,
): string {
	if (openTickets === 0) return "No open tickets";
	return `${plural(openTickets, "open ticket", "open tickets")}, ${assignedTickets} assigned`;
}

/** When Curo last applied a change from Jira, for the section's footer; null before anything was read. */
export function lastChangeLine(lastChangeAt: string | null): string | null {
	if (!lastChangeAt) return null;
	return `Last update from Jira: ${new Date(lastChangeAt).toLocaleString()}`;
}

/** What to do about a project whose import stopped part way. */
export const IMPORT_FAILED_ACTION =
	"Part of this project could not be read. Untick it, save, then approve it again to retry.";

/** Why the section could not load. */
export function workHealthErrorMessage(error: unknown): string {
	if (error instanceof ApiError && error.status === 404)
		return "Jira is no longer connected.";
	if (
		error instanceof ApiError &&
		error.code === "jira_integration_unavailable"
	)
		return "Jira work is unavailable right now. Please try again shortly.";
	return "Could not reach Curo. Please try again.";
}
